import { type SWRInfiniteConfiguration } from 'swr/infinite';
import {
  type PaginationQuery,
  type PaginationMeta,
} from '@mls-wallet/shared-types';
import { type PaginatedResponse, usePagination } from '@/hooks/use-pagination';
import type {
  AddressBookEntriesResponse,
  AddressBookEntryType,
  AddressBookListEntry,
} from '@/types/address-book';

type UseListAddressBookEntriesOptions = {
  type?: AddressBookEntryType;
  query?: string;
  pagination?: PaginationQuery;
  enabled?: boolean;
} & SWRInfiniteConfiguration<AddressBookEntriesResponse>;

/**
 * Fetch the current organization's address book with the shared cursor-based
 * pagination contract used elsewhere in ui-web.
 */
export function useListAddressBookEntries(
  options?: UseListAddressBookEntriesOptions
) {
  const { type, query, pagination, enabled, ...swr } = options ?? {};

  const result = usePagination<AddressBookListEntry>({
    path: 'organization/address-book',
    ...(type || query
      ? {
          query: {
            ...(type ? { type } : {}),
            ...(query ? { query } : {}),
          },
        }
      : {}),
    getItemKey: (entry) => entry.address,
    ...(pagination ? { pagination } : {}),
    ...(enabled !== undefined ? { enabled } : {}),
    ...(Object.keys(swr).length > 0
      ? {
          swr: swr as SWRInfiniteConfiguration<
            PaginatedResponse<AddressBookListEntry>
          >,
        }
      : {}),
  });

  return {
    entries: result.data,
    pagination: result.pagination as PaginationMeta,
    hasMore: result.hasMore,
    isLoading: result.isLoading,
    isLoadingMore: result.isLoadingMore,
    error: result.error,
    loadMore: result.loadMore,
    refresh: result.refetch,
    reset: result.reset,
  };
}
